import { bunja } from 'bunja';
import { atom } from 'jotai';
import { ReactNode } from 'react';

import { activeVouchersBunja } from '@flipster/features/src/reward-hub-page/v2/vouchers/state';
import { FormattedMessage } from '@flipster/i18n';
import { spotsBunja } from '@flipster/r230522/original/spots';
import { atomWithBigNumber } from '@flipster/react/misc/state/atom-with-bignumber';
import Num, { BigNumber } from '@flipster/utils/num';

import { tradeSymbolBunja } from '../../../symbol';
import { spotFormBunja } from './form';

interface FeeDiscountState {
  displayFeeRatePercent: BigNumber | null;
  message: ReactNode;
}

const resolveSpotFeeDiscount = (
  priceType: 'trigger' | 'market' | 'limit',
  estimatedTradingFee: BigNumber,
  voucherBalance: BigNumber,
): FeeDiscountState | null => {
  if (voucherBalance.isZero() || !['market', 'trigger'].includes(priceType)) return null;

  if (priceType === 'trigger' && voucherBalance.gt(0))
    return {
      displayFeeRatePercent: null,
      message: <FormattedMessage id='Preview.r260128.Feediscount_voucher_trigger' />,
    };

  if (priceType !== 'market') return null;

  if (voucherBalance.gte(estimatedTradingFee)) {
    return {
      displayFeeRatePercent: Num('0'),
      message: <FormattedMessage id='Preview.r260128.Feediscount_voucher' />,
    };
  }

  if (voucherBalance.gt(0)) {
    return {
      displayFeeRatePercent: null,
      message: <FormattedMessage id='Preview.r260128.Feediscount_voucher_partial_after_execution' />,
    };
  }

  return null;
};

const calcSpotEstimatedTradingFee = (params: {
  orderType: 'amount' | 'quantity';
  amount: BigNumber;
  quantity: BigNumber;
  price: string;
  takerFeeRate: BigNumber;
  unitOrderQty: BigNumber;
}): BigNumber => {
  const { orderType, amount, quantity, price, takerFeeRate, unitOrderQty } = params;
  if (orderType === 'amount') {
    return amount.multipliedBy(takerFeeRate);
  }
  const feeInBase = quantity.multipliedBy(takerFeeRate);
  if (feeInBase.isZero() || unitOrderQty.isZero()) {
    return Num(price).isZero() ? Num(0) : feeInBase.multipliedBy(price);
  }
  const stepsRoundedUp = feeInBase.dividedBy(unitOrderQty).decimalPlaces(0, BigNumber.ROUND_UP);
  const feeInBaseRoundedUp = stepsRoundedUp.multipliedBy(unitOrderQty);
  return feeInBaseRoundedUp.multipliedBy(price);
};

export const spotTradingFeeBunja = bunja(() => {
  const { symbol, symbolInfoWithDefaultAtom } = bunja.use(tradeSymbolBunja);
  const { spotsAtom } = bunja.use(spotsBunja);
  const { amountAtom, quantityAtom, orderStateAtom, calculationPriceAtom, priceTypeAtom } = bunja.use(spotFormBunja);
  const { spotFeeVoucherAvailableAmountAtom } = bunja.use(activeVouchersBunja);

  const takerFeeRateAtom = atom((get) => {
    const spots = get(spotsAtom);
    return Num(spots?.[symbol]?.takerFeeRate ?? '0');
  });

  const estimatedTradingFeeRatePercentAtom = atom((get) => {
    const takerFeeRate = get(takerFeeRateAtom);
    return takerFeeRate.multipliedBy(100).toString();
  });

  const estimatedTradingFeeAtom = atomWithBigNumber((get) => {
    const orderState = get(orderStateAtom);
    const amount = get(amountAtom);
    const quantity = get(quantityAtom);
    const price = get(calculationPriceAtom) ?? '0';
    const takerFeeRate = get(takerFeeRateAtom);
    const symbolInfo = get(symbolInfoWithDefaultAtom);
    const unitOrderQty = Num(symbolInfo?.unitOrderQty ?? '0');
    return calcSpotEstimatedTradingFee({
      orderType: orderState.type,
      amount,
      quantity,
      price,
      takerFeeRate,
      unitOrderQty,
    });
  });

  const feeDiscountStateAtom = atom((get) => {
    const estimatedTradingFee = get(estimatedTradingFeeAtom);
    const voucherBalance = get(spotFeeVoucherAvailableAmountAtom);
    const priceType = get(priceTypeAtom);
    return resolveSpotFeeDiscount(priceType, estimatedTradingFee, voucherBalance);
  });

  const isFullDiscountAtom = atom((get) => {
    const state = get(feeDiscountStateAtom);
    return state?.displayFeeRatePercent && state.displayFeeRatePercent.isZero();
  });

  return {
    takerFeeRateAtom,
    estimatedTradingFeeRatePercentAtom,
    feeDiscountStateAtom,
    isFullDiscountAtom,
  };
});

spotTradingFeeBunja.debugLabel = 'spotTradingFeeBunja';
